import { useQueries } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';
import { salesApi } from '../../../api/endpoints';
import { noteBasisQuery, noteSourcesQuery } from '../../../api/queries';
import type { InvoiceKind, NoteSource } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { AsyncComboBox } from '../../../ui/ComboBox';
import { Code, Money } from '../../../ui/Layout';
import styles from '../Sales.module.css';
import { sourceFromBasis } from './noteReferences';

/**
 * The option and the chosen row read in one order (AC-N7): number · total · remaining · date ·
 * identifier. `remaining` (the still-reducible amount) shows only for a credit note, where the
 * server returns it; a debit note carries none. Before the first choice an option ends with the
 * buyer's name, which tells apart invoices of different buyers; a chosen row never repeats it (the
 * buyer is shown once, read-only, under the list).
 */
function ReferenceLine({ source, withBuyer }: { source: NoteSource; withBuyer: boolean }) {
  const { formatDate, formatDateTime } = useI18n();
  // The document date: the local calendar date when the list carries it, else the issue timestamp.
  const dateText = source.issueDate
    ? formatDate(source.issueDate)
    : source.dateTimeIssued
      ? formatDateTime(source.dateTimeIssued)
      : '—';
  const segments: ReactNode[] = [
    <span key="number" className={styles.mono} dir="ltr">
      {source.number ?? '—'}
    </span>,
    <Money key="total" value={source.totalAmount} currency="EGP" />,
    ...(source.remaining !== null
      ? [<Money key="remaining" value={source.remaining} currency="EGP" />]
      : []),
    <span key="date">{dateText}</span>,
    <Code key="eta">{source.etaId ?? '—'}</Code>,
    ...(withBuyer && source.receiverName
      ? [<UserText key="buyer">{source.receiverName}</UserText>]
      : []),
  ];
  return (
    <span className={styles.referenceLine}>
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {index > 0 ? <span aria-hidden="true">·</span> : null}
          {segment}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Step 1 of the credit/debit-note editor, «المستند المرجعي» (notes-editor.md:31-55). The reference
 * comes first and the buyer follows from it (RN4-02): one rule line, one add field that opens only
 * after typing (`opensOn="input"`, `clearOnPick`) over the organization's eligible invoices, the
 * chosen references listed under it each with «أخرِج» / "Take out" (RN4R-01, never "Remove"), and
 * the buyer read-only beneath, tagged «من الفاتورة المرجعية».
 *
 * There is no checkbox, switch, select-all or per-row add/remove verb at any width, no count of
 * chosen references in a heading or label, and no text asking for a buyer or saying a buyer loads
 * invoices (AC-N7, AC-N9). The catalogue picker, the new-item form and the new-buyer form never
 * appear here (AC-N1).
 */
export function ReferenceStep({
  orgId,
  kind,
  references,
  picked,
  issueOf,
  onPick,
  onTakeOut,
}: {
  orgId: string;
  kind: InvoiceKind;
  /** The ids of the chosen references (the draft's own field), in the note's order. */
  references: readonly string[];
  /** Sources picked this session, by id; the rest resolve through `note-basis`. */
  picked: Record<string, NoteSource>;
  issueOf: (path: string) => string | undefined;
  onPick: (source: NoteSource) => void;
  onTakeOut: (id: string) => void;
}) {
  const { t } = useI18n();
  // Ids not held from a pick are read back through `note-basis` (a `?from=` deep link, a reopen).
  const unresolved = references.filter((id) => !picked[id]);
  const resolvedResults = useQueries({
    queries: unresolved.map((id) => ({ ...noteBasisQuery(orgId, id, kind) })),
  });
  const resolved: Record<string, NoteSource> = {};
  unresolved.forEach((id, index) => {
    const data = resolvedResults[index]?.data;
    if (data) resolved[id] = sourceFromBasis(data);
  });

  const chosen = references.flatMap((id) => {
    const source = picked[id] ?? resolved[id];
    return source ? [source] : [];
  });
  const chosenIds = new Set(references);
  // The first choice fixes the buyer; later searches are narrowed to that buyer (when identified).
  const first = chosen.length ? chosen[0] : null;
  const lockedPartyId = first ? first.receiverPartyId : null;
  const buyerName = first ? (first.receiverName ?? t.sales.cash) : null;
  const sourcesParams = (q: string) => ({
    kind,
    ...(q ? { q } : {}),
    ...(lockedPartyId ? { receiverPartyId: lockedPartyId } : {}),
  });

  return (
    <div className={styles.stepBody}>
      <p className={styles.note}>{t.editor.note.rule}</p>

      <div className={styles.referenceAddField} data-testid="reference-add-field">
        <AsyncComboBox<NoteSource>
          label={t.editor.note.addField}
          description={t.editor.note.addHint}
          fieldName="add-reference"
          value={null}
          selectedItem={null}
          clearOnPick
          opensOn="input"
          errorMessage={issueOf('references')}
          onChange={(source) => {
            if (source) onPick(source);
          }}
          queryFor={(q) => {
            const params = sourcesParams(q);
            return {
              queryKey: noteSourcesQuery(orgId, params).queryKey,
              queryFn: async ({ signal }) => {
                const res = await salesApi.noteSources(orgId, params, signal);
                // An already-chosen invoice is not offered again.
                return res.items.filter((item) => !chosenIds.has(item.id));
              },
              staleTime: 0,
            };
          }}
          getKey={(source) => source.id}
          getLabel={(source) => source.number ?? source.id}
          renderItem={(source) => <ReferenceLine source={source} withBuyer={chosen.length === 0} />}
        />
      </div>

      {chosen.length ? (
        <ol className={styles.referenceList} data-testid="reference-list">
          {chosen.map((source) => (
            <li
              key={source.id}
              className={styles.referenceItem}
              data-testid={`reference-${source.id}`}
            >
              <ReferenceLine source={source} withBuyer={false} />
              <Button
                variant="ghost"
                size="sm"
                className={styles.textButton}
                aria-label={`${t.editor.note.takeOut} ${source.number ?? source.id}`}
                onPress={() => onTakeOut(source.id)}
                testId="reference-out"
              >
                {t.editor.note.takeOut}
              </Button>
            </li>
          ))}
        </ol>
      ) : null}

      {chosen.length ? (
        <div className={styles.readOnlyBlock} data-testid="reference-buyer">
          <span className={styles.customerName}>
            {t.editor.note.buyer('')}
            <UserText>{buyerName}</UserText>
          </span>
          <p className={styles.readOnlyNote}>{t.editor.note.fromReference}</p>
        </div>
      ) : null}
    </div>
  );
}
